'use client';

import { useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';
import { Stack, TagsInput, Box, Group, ScrollArea } from '@mantine/core';
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import dialogStoreVar from '@features/Dialog/store/dialogStore';
import { useRedactEntitiesJobMutation } from '@app-types/blackroom-api';
import { palette } from '@app/shared/ui-kit/colors';
import { dialogRedaction, searchStepGrid } from './styles.css';
import { DialogRedactKeywordsProps } from './types';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';
import { Center } from '@mantine/core';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export default function DialogRedactKeywords({
  then,
  ...dialogProps
}: DialogRedactKeywordsProps) {
  /* ********************************** HOOKS ********************************* */

  // Context
  const dialogStore = useReactiveVar(dialogStoreVar);
  const workspace = useWorkspace();
  const { t } = useTranslation(['Common', 'Resource', 'Redaction']);

  // State
  const [keywords, setKeywords] = useState<string[]>([]);

  // API
  const [redactEntitiesJob] = useRedactEntitiesJobMutation();

  /* ***************************** LOCAL VARIABLES **************************** */

  const confirm = async () => {
    try {
      await redactEntitiesJob({
        variables: {
          filter: {
            workspaceId: workspace.id,
            keywords,
          },
          action: 'redact',
        },
      });

      then?.();

      showNotification({
        title: t('Resource:Common.operation.redact.sentByEmail.title'),
        message: t('Resource:Common.operation.redact.sentByEmail.message'),
        color: 'blackroomBlue',
      });
    } catch (error) {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'blackroomRed',
      });
      console.error(error);
    }

    dialogStore?.closeDialog?.('dialog-redaction');
  };

  /* ***************************** RENDER HELPERS ***************************** */

  function renderSearchStep() {
    const SearchStepLabel = ({ children }: { children: React.ReactNode }) => (
      <Group h={42} align="center">
        {children}
      </Group>
    );

    return (
      <ScrollArea
        w="100%"
        h="100%"
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Center fz={24} fw="bold">
          {t('Redaction:Common.cta.redactKeywords')}
        </Center>

        <Box fz={14} fw="300" pt="lg" pb="xs">
          {t('Redaction:DialogRedaction.keywordsHelper')}
        </Box>

        <Box className={searchStepGrid} w="100%">
          <SearchStepLabel>
            {t('Redaction:Common.redactSearchArgs.keywords')}
          </SearchStepLabel>
          <TagsInput
            size="md"
            h={52}
            py={0}
            variant="unstyled"
            splitChars={[';']}
            value={keywords}
            placeholder={t(
              'Redaction:DialogRedaction.search.insertKeywordPlaceholder',
            )}
            onChange={(value) => setKeywords(value)}
          />
        </Box>

        <Center fz={13} fw="300" c={palette.blackroomGray}>
          {t('Redaction:DialogRedaction.enterKeyword')}
        </Center>
      </ScrollArea>
    );
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <Stack className={dialogRedaction}>
      <Stack flex={1} mih={0} w="100%" justify="start">
        {renderSearchStep()}
      </Stack>

      <ConfirmBar
        w="100%"
        disabled={keywords.length === 0}
        onConfirm={confirm}
        onCancel={() => dialogStore?.closeDialog?.('dialog-redaction')}
        labelProps={{
          cancel: t('Common:prompts.cancel'),
          confirm: t('Common:prompts.confirm'),
        }}
      />
    </Stack>
  );
}
